'use client';

import { createContext, useCallback, useContext } from 'react';

import { pushGAEvent } from '@widget/analytics';

import type { FC, ReactNode } from 'react';

type ComponentTrackingInfo = {
  componentType: string;
  componentTrackingName?: string;
};

type ProviderProps = {
  children: ReactNode;
  value: ComponentTrackingInfo;
};

type DLProviderValueProp = {
  pushClickEvent: (data: { text: string; info?: string; url?: string }) => void;
  value: ComponentTrackingInfo;
};

const defaultValues: DLProviderValueProp = {
  value: { componentType: '' },
  pushClickEvent: () => undefined,
};

const DataLayerContext = createContext<DLProviderValueProp>(defaultValues);

export const DLProvider: FC<ProviderProps> = ({ value, children }): JSX.Element => {
  const pushClickEvent = useCallback(
    ({ text, info, url }: { text: string; info?: string; url?: string }) => {
      const componentInfo = `${value.componentType}${
        value.componentTrackingName ? '/' + value.componentTrackingName : ''
      }`;

      if (url && isExternalUrl(url)) {
        pushGAEvent('outbound_click', {
          info: url,
          label: new URL(url).host,
        });
      } else {
        pushGAEvent('click-event', {
          buttonGroup: info || componentInfo,
          buttonText: text ?? '',
          buttonUrl: url,
        });
      }
    },
    [value.componentTrackingName, value.componentType],
  );
  return (
    <DataLayerContext.Provider value={{ value, pushClickEvent }}>
      {children}
    </DataLayerContext.Provider>
  );
};

export const usePushDataLayerEvent = (): DLProviderValueProp['pushClickEvent'] => {
  return useContext(DataLayerContext).pushClickEvent;
};

type TrackPushEventProps = {
  children: ReactNode;
  component?: 'span';
};

export const DLTrackLinkClicks = ({ children, component }: TrackPushEventProps): JSX.Element => {
  const pushClickEvent = usePushDataLayerEvent();

  const handleOnClick = (event: React.MouseEvent<HTMLElement>): void => {
    const target = event.target as HTMLAnchorElement;

    if (['a', 'button'].includes(target.tagName.toLowerCase())) {
      pushClickEvent({ text: target.innerText, info: target.dataset.gaInfo, url: target.href });
    }
  };

  const TagComponent = component || 'div';
  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
  return <TagComponent onClick={handleOnClick}>{children}</TagComponent>;
};

function isExternalUrl(url: string) {
  const currentUrl = new URL(window.location.href);
  const linkUrl = new URL(url);

  return currentUrl.origin !== linkUrl.origin;
}
